import React, { useState } from "react";
import { Star, Send, Check, Loader2 } from "lucide-react";
import { kidOfTheWeek } from "../mock";
import Reveal from "./Reveal";

const KidOfTheWeek = () => {
  const [form, setForm] = useState({ braveName: "", age: "", region: "", inspiration: "", adultName: "", adultPhone: "", adultEmail: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.braveName || !form.age || !form.region || !form.inspiration || !form.adultName || !form.adultPhone || !form.adultEmail) {
      setError("Please fill out every field so we can celebrate your Brave Me kid!");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/nominate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || "Something went wrong. Please try again.");
        return;
      }
      setSent(true);
      setForm({ braveName: "", age: "", region: "", inspiration: "" });
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kid-of-the-week" className="py-16 md:py-24 bg-white" style={{ background: "linear-gradient(180deg, #ffffff 0%, rgba(24,126,228,0.06) 100%)" }}>
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <Reveal>
          <div className="text-center mb-12">
            <p className="font-display tracking-[0.25em] uppercase text-sm" style={{ color: "#FF6C10" }}>{kidOfTheWeek.eyebrow}</p>
            <h2 className="mt-2 font-display text-4xl md:text-6xl" style={{ color: "#187EE4" }}>{kidOfTheWeek.title}</h2>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
          <Reveal>
            <div className="relative h-full rounded-[28px] overflow-hidden shadow-xl border-4 border-white min-h-[360px]">
              <img src={kidOfTheWeek.image} alt="Brave Me Kid of the Week" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute top-5 left-5 flex items-center gap-2 px-4 py-2 rounded-full shadow-lg" style={{ backgroundColor: "#FF6C10" }}>
                <Star className="w-5 h-5 text-white" fill="white" />
                <span className="font-display text-white text-sm">Newest Member of Riley&rsquo;s Roariers</span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="h-full bg-white rounded-[28px] shadow-xl border border-slate-100 p-6 md:p-8">
              <p className="text-slate-600 font-medium leading-relaxed">{kidOfTheWeek.body}</p>

              {sent ? (
                <div className="mt-6 flex flex-col items-center text-center py-8" data-testid="nominate-success">
                  <span className="flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ backgroundColor: "rgba(24,126,228,0.12)" }}>
                    <Check className="w-8 h-8" style={{ color: "#187EE4" }} strokeWidth={3} />
                  </span>
                  <h3 className="font-display text-2xl" style={{ color: "#187EE4" }}>Nomination received!</h3>
                  <p className="mt-2 text-slate-600 font-medium">Thank you for sharing your Brave Me kid with us.</p>
                  <button onClick={() => setSent(false)} className="mt-5 font-display text-sm underline" style={{ color: "#FF6C10" }}>Nominate another hero</button>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-6 space-y-4">
                  <div>
                    <label className="block font-bold text-sm text-slate-700 mb-1.5">Brave Name</label>
                    <input value={form.braveName} onChange={update("braveName")} placeholder="Their name" data-testid="nominate-name" className="w-full rounded-xl border-2 border-slate-200 focus:border-[#187EE4] outline-none px-4 py-3 font-medium text-slate-700 transition-colors" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-bold text-sm text-slate-700 mb-1.5">Age</label>
                      <input type="number" min="0" max="120" value={form.age} onChange={update("age")} placeholder="Age" data-testid="nominate-age" className="w-full rounded-xl border-2 border-slate-200 focus:border-[#187EE4] outline-none px-4 py-3 font-medium text-slate-700 transition-colors" />
                    </div>
                    <div>
                      <label className="block font-bold text-sm text-slate-700 mb-1.5">Region</label>
                      <select value={form.region} onChange={update("region")} data-testid="nominate-region" className="w-full rounded-xl border-2 border-slate-200 focus:border-[#187EE4] outline-none px-4 py-3 font-medium text-slate-700 bg-white transition-colors">
                        <option value="">Select a region</option>
                        {kidOfTheWeek.regions.map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block font-bold text-sm text-slate-700 mb-1.5">A few ways they inspire you</label>
                    <textarea value={form.inspiration} onChange={update("inspiration")} rows={4} placeholder="Tell us what makes them so brave…" data-testid="nominate-inspiration" className="w-full rounded-xl border-2 border-slate-200 focus:border-[#187EE4] outline-none px-4 py-3 font-medium text-slate-700 resize-none transition-colors" />
                  </div>

                  <div className="pt-2 border-t border-slate-100">
                    <p className="font-display text-sm uppercase tracking-wide mb-3" style={{ color: "#187EE4" }}>Grown-up submitting</p>
                    <div className="space-y-4">
                      <div>
                        <label className="block font-bold text-sm text-slate-700 mb-1.5">Your Name</label>
                        <input value={form.adultName} onChange={update("adultName")} placeholder="Parent / guardian name" data-testid="nominate-adult-name" className="w-full rounded-xl border-2 border-slate-200 focus:border-[#187EE4] outline-none px-4 py-3 font-medium text-slate-700 transition-colors" />
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block font-bold text-sm text-slate-700 mb-1.5">Phone Number</label>
                          <input type="tel" value={form.adultPhone} onChange={update("adultPhone")} placeholder="(555) 123-4567" data-testid="nominate-adult-phone" className="w-full rounded-xl border-2 border-slate-200 focus:border-[#187EE4] outline-none px-4 py-3 font-medium text-slate-700 transition-colors" />
                        </div>
                        <div>
                          <label className="block font-bold text-sm text-slate-700 mb-1.5">Email</label>
                          <input type="email" value={form.adultEmail} onChange={update("adultEmail")} placeholder="you@email.com" data-testid="nominate-adult-email" className="w-full rounded-xl border-2 border-slate-200 focus:border-[#187EE4] outline-none px-4 py-3 font-medium text-slate-700 transition-colors" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {error && <p className="text-sm font-semibold" style={{ color: "#FF1228" }} data-testid="nominate-error">{error}</p>}
                  <button type="submit" disabled={loading} data-testid="nominate-submit" className="w-full flex items-center justify-center gap-2 font-display text-white text-lg py-4 rounded-full transition-transform hover:scale-[1.02] shadow-lg disabled:opacity-70" style={{ backgroundColor: "#FF6C10" }}>
                    {loading ? (<><Loader2 className="w-5 h-5 animate-spin" /> Sending…</>) : (<><Send className="w-5 h-5" /> Nominate Your Hero</>)}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default KidOfTheWeek;
